import * as React from 'react';

import { parseCalendarDate } from '@rematter/common/dateTime';
import {
  DataGridColumn,
  LocalizedDate,
  Badge,
  Menu,
  Button,
} from '@rematter/ui-web';
import { LucideEllipsis } from 'lucide-react';

import { OrganizationListRecord } from '../../../_graphql/queryOrganizationList';
import { useTranslations } from '../../../i18n/useTranslations';

type UseOrganizationListColumnsParams = {
  onEdit: (row: OrganizationListRecord) => void;
  onViewDetails: (row: OrganizationListRecord) => void;
  onSetOnboarded: (args: {
    organizationId: string;
    onboarded: boolean;
  }) => void;
  onReset: (row: OrganizationListRecord) => void;
};

export function useOrganizationListColumns({
  onEdit,
  onViewDetails,
  onSetOnboarded,
  onReset,
}: UseOrganizationListColumnsParams): DataGridColumn<OrganizationListRecord>[] {
  const t = useTranslations();

  return React.useMemo(
    () => [
      {
        key: 'name',
        header: t.organizationListView.header.name,
        width: 250,
        minWidth: 200,
        cell: (cell) => (
          <div className="font-medium text-neutral/90">{cell.row.name}</div>
        ),
      },
      {
        key: 'domain',
        header: t.organizationListView.header.domain,
        width: 200,
        minWidth: 150,
        cell: (cell) => (
          <div className="text-neutral/60">{cell.row.domain}</div>
        ),
      },
      {
        key: 'createdAt',
        header: t.organizationListView.header.createdAt,
        width: 150,
        minWidth: 120,
        cell: (cell) => {
          const date = parseCalendarDate(cell.row.createdAt);
          return <LocalizedDate date={date} dateStyle="medium" />;
        },
      },
      {
        key: 'updatedAt',
        header: t.organizationListView.header.updatedAt,
        width: 150,
        minWidth: 120,
        cell: (cell) => {
          const date = parseCalendarDate(cell.row.updatedAt);
          return <LocalizedDate date={date} dateStyle="medium" />;
        },
      },
      {
        key: 'userCount',
        header: t.organizationListView.header.userCount,
        width: 100,
        minWidth: 80,
        align: 'center',
        cell: (cell) => (
          <div className="text-neutral/70">{cell.row.userCount}</div>
        ),
      },
      {
        key: 'status',
        header: t.organizationListView.header.status,
        width: 100,
        minWidth: 100,
        cell: (cell) => (
          <Badge color={cell.row.onboardedAt ? 'success' : 'warning'}>
            {cell.row.onboardedAt
              ? t.organizationListView.status.onboarded
              : t.organizationListView.status.pending}
          </Badge>
        ),
      },
      {
        key: 'actions',
        header: t.organizationListView.header.actions,
        width: 50,
        minWidth: 50,
        align: 'center',
        cell: (cell) => (
          <Menu
            trigger={
              <Button size="small" variant="ghost">
                <LucideEllipsis className="size-4" />
              </Button>
            }
            items={[
              {
                label: t.organizationListView.actions.edit,
                onAction: () => onEdit(cell.row),
              },
              {
                label: t.organizationListView.actions.viewDetails,
                onAction: () => onViewDetails(cell.row),
              },
              {
                label: t.organizationListView.actions.setAsOnboarded,
                onAction: () =>
                  onSetOnboarded({
                    organizationId: cell.row.id,
                    onboarded: true,
                  }),
              },
              'separator',
              {
                label: t.organizationListView.actions.reset,
                onAction: () => onReset(cell.row),
                className: 'text-destructive',
              },
            ]}
          />
        ),
      },
    ],
    [t, onEdit, onViewDetails, onSetOnboarded, onReset],
  );
}
